import { parseLocalDateString } from './dateUtils';
import { PAYROLL_CUTOVER_DATE } from './payrollTaxUtils';

const VAT_RATE = 0.12;
export const PROMO_PACKAGE_SESSION_COUNT = 10;
/** Promo packages starting on/after this date issue a per-session center receipt for all 10 sessions. */
export const PROMO_CENTER_RECEIPT_CUTOVER_DATE = '2026-08-13';

export function roundCurrency(amount) {
  return Math.round((Number(amount) || 0) * 100) / 100;
}

export function shouldUseSplitReceipts(therapist, sessionDate) {
  if (!therapist?.sworn_declaration) {
    return false;
  }

  const session = parseLocalDateString(sessionDate);
  const cutover = parseLocalDateString(PAYROLL_CUTOVER_DATE);
  return session >= cutover;
}

export function calculateReceiptSplit({
  totalDue,
  therapistFee,
  isNewPromoPackage = false,
  promoSessionCount = PROMO_PACKAGE_SESSION_COUNT,
}) {
  const total = roundCurrency(totalDue);
  const therapist = roundCurrency(therapistFee);

  if (isNewPromoPackage) {
    const perSessionAmount = roundCurrency(total / promoSessionCount);
    const center = roundCurrency(Math.max(0, perSessionAmount - therapist));

    return {
      totalDue: total,
      therapistFee: therapist,
      centerFee: center,
      perSessionAmount,
    };
  }

  const center = roundCurrency(Math.max(0, total - therapist));

  return {
    totalDue: total,
    therapistFee: therapist,
    centerFee: center,
  };
}

export function getCenterVatBreakdown(centerFee) {
  const totalSales = roundCurrency(centerFee);

  if (totalSales <= 0) {
    return {
      totalSales: 0,
      preVatAmount: 0,
      vatAmount: 0,
      amountDue: 0,
    };
  }

  const preVatAmount = roundCurrency(totalSales / (1 + VAT_RATE));
  const vatAmount = roundCurrency(totalSales - preVatAmount);

  return {
    totalSales,
    preVatAmount,
    vatAmount,
    amountDue: totalSales,
  };
}

export function getSameDayReceiptMode(sessions, sessionDate) {
  if (!sessions?.length) {
    return null;
  }

  const modes = sessions.map((session) => shouldUseSplitReceipts(session.therapist, sessionDate));
  if (modes.every(Boolean)) {
    return 'split';
  }
  if (modes.every((mode) => !mode)) {
    return 'center';
  }
  return 'mixed';
}

export function buildCombinedSplitReceiptSummary(sessionLines) {
  const enrichedLines = sessionLines.map((line) => ({
    ...line,
    ...calculateReceiptSplit({
      totalDue: line.totalDue,
      therapistFee: line.therapistFee,
    }),
  }));

  const combinedTotalDue = roundCurrency(
    enrichedLines.reduce((sum, line) => sum + line.totalDue, 0)
  );
  const combinedCenterFee = roundCurrency(
    enrichedLines.reduce((sum, line) => sum + line.centerFee, 0)
  );
  const combinedTherapistFees = roundCurrency(
    enrichedLines.reduce((sum, line) => sum + line.therapistFee, 0)
  );

  return {
    sessionLines: enrichedLines,
    combinedTotalDue,
    combinedCenterFee,
    combinedTherapistFees,
    centerVat: getCenterVatBreakdown(combinedCenterFee),
  };
}

/** VAT breakdown for center-issued ORs (matches single-session VATBreakdown logic). */
export function getCenterReceiptVatBreakdown({
  combinedBaseRate,
  isPwd = false,
  applyPwdDiscount = true,
}) {
  const totalSales = roundCurrency(combinedBaseRate);

  if (totalSales <= 0) {
    return {
      totalSales: 0,
      preVatAmount: 0,
      vatAmount: 0,
      pwdDiscount: 0,
      amountDue: 0,
    };
  }

  const preVatAmount = roundCurrency(totalSales / (1 + VAT_RATE));
  const vatAmount = roundCurrency(totalSales - preVatAmount);
  const pwdDiscount =
    isPwd && applyPwdDiscount ? roundCurrency(preVatAmount * 0.2) : 0;
  const amountDue = roundCurrency(preVatAmount - pwdDiscount + vatAmount);

  return {
    totalSales,
    preVatAmount,
    vatAmount,
    pwdDiscount,
    amountDue,
  };
}

export function buildCombinedCenterReceiptSummary(sessionLines, { isPwd = false } = {}) {
  const combinedTotalDue = roundCurrency(
    sessionLines.reduce((sum, line) => sum + line.totalDue, 0)
  );
  const combinedBaseRate = roundCurrency(
    sessionLines.reduce((sum, line) => sum + (Number(line.baseRate) || 0), 0)
  );
  const applyPwdDiscount = !sessionLines.every((line) => line.sessionType === 'IE');

  return {
    sessionLines,
    combinedTotalDue,
    combinedBaseRate,
    centerVat: getCenterReceiptVatBreakdown({
      combinedBaseRate,
      isPwd,
      applyPwdDiscount,
    }),
  };
}

export function isPromoReceiptSession({
  session,
  matchingService,
  isCurrentSession = false,
  isPromoFormValue = false,
}) {
  if (session.attendance?.promo_session != null) {
    return true;
  }

  if (isCurrentSession && isPromoFormValue) {
    return true;
  }

  if (matchingService?.promo_count > 0 && matchingService?.promo_count < 10) {
    return true;
  }

  return false;
}

export function isOngoingPromoSession({
  session,
  matchingService,
  isPromoFormValue = false,
}) {
  if (isPromoFormValue && (matchingService?.promo_count ?? 0) === 0) {
    return false;
  }

  const promoSessionNumber = session.attendance?.promo_session;
  if (promoSessionNumber != null) {
    return promoSessionNumber >= 2;
  }

  const promoCount = matchingService?.promo_count ?? 0;
  return promoCount > 0 && promoCount < 10;
}

/** First session of a new 10-session promo package (package payment collected today). */
export function isNewPromoSession({
  session,
  matchingService,
  isPromoFormValue = false,
  isCurrentSession = true,
}) {
  if (session.attendance?.promo_session === 1) {
    return true;
  }

  return (
    isPromoFormValue &&
    isCurrentSession &&
    (matchingService?.promo_count ?? 0) === 0
  );
}

export function isPromoPackageOnOrAfterCenterReceiptCutover(packageStartDate) {
  if (!packageStartDate) return false;

  const start = parseLocalDateString(String(packageStartDate).slice(0, 10));
  const cutover = parseLocalDateString(PROMO_CENTER_RECEIPT_CUTOVER_DATE);
  return start >= cutover;
}

export function sortPromoAttendanceRows(rows = []) {
  return [...rows].sort((a, b) => {
    const dateCmp = String(a.attendance_date).localeCompare(String(b.attendance_date));
    if (dateCmp !== 0) return dateCmp;
    return (Number(a.promo_session) || 0) - (Number(b.promo_session) || 0);
  });
}

export function findPromoCycleStarterIndex(sortedRows, sessionDate) {
  const sessionDateStr = String(sessionDate).slice(0, 10);
  let starterIndex = -1;

  sortedRows.forEach((row, index) => {
    if (
      Number(row.promo_session) === 1 &&
      String(row.attendance_date).slice(0, 10) <= sessionDateStr
    ) {
      starterIndex = index;
    }
  });

  return starterIndex;
}

export function getMaxPaidInPromoCycle(sortedRows, starterIndex) {
  if (starterIndex < 0) return 0;

  let maxPaid = Number(sortedRows[starterIndex]?.amount_paid) || 0;
  for (let i = starterIndex + 1; i < sortedRows.length; i += 1) {
    if (Number(sortedRows[i].promo_session) === 1) break;
    maxPaid = Math.max(maxPaid, Number(sortedRows[i].amount_paid) || 0);
  }

  return maxPaid;
}

export function resolvePromoSplitReceiptContext({
  session,
  matchingService,
  isPromoFormValue = false,
  promoCycleRows = [],
  sessionDate,
}) {
  const isOngoing = isOngoingPromoSession({ session, matchingService, isPromoFormValue });
  const isStartingNewPackage =
    isPromoFormValue && (matchingService?.promo_count ?? 0) === 0;
  const isFirstSavedSession = session.attendance?.promo_session === 1;

  if (!isStartingNewPackage && !isFirstSavedSession && !isOngoing) {
    return {
      usePerSessionCenterReceipt: false,
      therapistReceiptOnly: false,
      packageTotal: null,
      packageStartDate: null,
    };
  }

  if (isStartingNewPackage) {
    return {
      usePerSessionCenterReceipt: isPromoPackageOnOrAfterCenterReceiptCutover(sessionDate),
      therapistReceiptOnly: false,
      packageTotal: null,
      packageStartDate: sessionDate,
    };
  }

  const sorted = sortPromoAttendanceRows(
    promoCycleRows.filter((row) => row.status === 'present' && row.promo_session != null)
  );
  const starterIndex = findPromoCycleStarterIndex(sorted, sessionDate);
  const packageStartDate =
    starterIndex >= 0 ? sorted[starterIndex].attendance_date : sessionDate;
  const packageTotal =
    starterIndex >= 0
      ? getMaxPaidInPromoCycle(sorted, starterIndex)
      : Number(session.attendance?.amount_paid) || null;
  const onOrAfterCutover = isPromoPackageOnOrAfterCenterReceiptCutover(packageStartDate);

  if (isFirstSavedSession) {
    return {
      usePerSessionCenterReceipt: onOrAfterCutover,
      therapistReceiptOnly: false,
      packageTotal,
      packageStartDate,
    };
  }

  return {
    usePerSessionCenterReceipt: onOrAfterCutover,
    therapistReceiptOnly: isOngoing && !onOrAfterCutover,
    packageTotal,
    packageStartDate,
  };
}
